/**
 * The attempt timeline (docs/plans/oncall-line-plan.md 2, 7.7): who rang, in
 * which wave, and what happened to each phone. Waves are the phones rung
 * together (two at a time by default); wave 0 is the backup phones the line
 * dials when it cannot reach its own list (plan 4 layer 3).
 *
 * Staff numbers never reach this view (plan 16, WP-10): an attempt carries
 * the member's name only, and a backup phone reads as "Backup phone".
 */
import { Users } from 'lucide-react';
import type { OncallCallAttempt } from '../../api/oncallApi';
import { formatTime } from '../../lib/format';
import { groupAttempts, outcomeMeta } from './callMeta';

interface AttemptTimelineProps {
  attempts: OncallCallAttempt[];
  timeZone: string | null | undefined;
  /** The call is still ringing (an attempt with no outcome yet reads "Ringing"). */
  ringing?: boolean;
}

export default function AttemptTimeline({ attempts, timeZone, ringing = false }: AttemptTimelineProps) {
  const waves = groupAttempts(attempts);
  if (waves.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="oncall-attempts-empty">
        No phone was rung for this call.
      </p>
    );
  }
  return (
    <ol className="space-y-3" data-testid="oncall-attempts">
      {waves.map((wave) => (
        <li key={wave.waveNo} className="relative border-l-2 border-gray-200 pl-3 dark:border-gray-700">
          <p className="flex flex-wrap items-baseline gap-x-2 text-xs font-medium text-gray-500 dark:text-gray-400">
            <span className="uppercase tracking-wider">{wave.label}</span>
            {wave.rangAt && <span className="tabular-nums">{formatTime(wave.rangAt, timeZone)}</span>}
          </p>
          <ul className="mt-1 space-y-1">
            {wave.attempts.map((a) => {
              const outcome = outcomeMeta(a.outcome, ringing);
              const who = a.member_name || (a.member_id ? 'Team member' : 'Backup phone');
              return (
                <li
                  key={`${wave.waveNo}-${a.attempt_no}`}
                  className="flex flex-wrap items-center justify-between gap-x-3 text-sm"
                  data-testid="oncall-attempt"
                >
                  <span className="flex min-w-0 items-center gap-1.5 text-gray-900 dark:text-gray-100">
                    {!a.member_id && <Users className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" aria-hidden="true" />}
                    <span className="truncate">{who}</span>
                  </span>
                  <span className={`text-xs font-medium ${outcome.cls}`} title={outcome.hint}>
                    {outcome.label}
                  </span>
                </li>
              );
            })}
          </ul>
        </li>
      ))}
    </ol>
  );
}
